/**
 * Pod demand — a Solution's hours by who carries them on the Pod, on the Pod & Fit tab.
 *
 * `PodDemandChart`: 13 monthly stacked bars (6 back, current, 6 forward). Past months are
 * logged actuals, the current and forward months are NOS guidance. Each Primary Position
 * is one band in its Discipline's colour; Supporting (plus anyone who logged or is
 * forecast here without an Assignment) is one neutral band. The Intensity benchmark line
 * and a "Today" divider carry over from the old Forecast-tab Workload chart
 * (docs/specs/visualize-solution-workload.md), which this replaces.
 *
 * `MemberDemand`: one Resource's logged vs projected hours/month and a 13-bar sparkline,
 * for a Pod card or Supporting row.
 *
 * 🔒 Display-only. Hours never feed points, Intensity or the Fit Score.
 * Hand-rolled SVG — no chart library exists in this stack.
 */

import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import type { SolutionWorkloadPayload } from "@/hooks/use-solution-workload";
import {
  type DemandGroupKey,
  groupDemandByPod,
  type PodDemandMonth,
  SUPPORTING_GROUP,
  summarizeResourceDemand,
} from "@/lib/pod-demand";

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function monthLabel(key: string): { abbr: string; year: number } {
  const [yearStr, monthStr] = key.split("-");
  return { abbr: MONTH_ABBR[Number(monthStr) - 1] ?? "", year: Number(yearStr) };
}

/** A Discipline colour softened against the card, for projected (guidance) months. */
function projectedTint(color: string): string {
  return `color-mix(in oklab, ${color} 38%, var(--card))`;
}

/** Data considered stale past 30 days without a sync — matches the ~monthly aggregation cadence. */
const STALE_AFTER_MS = 30 * 24 * 60 * 60 * 1000;

function relativeTimeFrom(iso: string, now: Date): string {
  const diffMin = Math.round((now.getTime() - new Date(iso).getTime()) / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMonth = Math.round(diffDay / 30);
  return diffMonth < 12 ? `${diffMonth}mo ago` : `${Math.round(diffMonth / 12)}y ago`;
}

/** One band in the stack: a Primary Position or the Supporting group. */
export interface DemandGroup {
  key: DemandGroupKey;
  /** "R1 · Raj Rajagopal", "Supporting". */
  label: string;
  /** Discipline colour (a library hex) or a token for Supporting. */
  color: string;
}

const CHART_W = 640;
const CHART_H = 200;
const PAD_LEFT = 32;
const PAD_RIGHT = 12;
const PAD_TOP = 18;
const PAD_BOTTOM = 22;
const PLOT_W = CHART_W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = CHART_H - PAD_TOP - PAD_BOTTOM;
const BAR_W = 24;
const Y_TICKS = [0, 0.5, 1] as const;

function x(index: number, count: number): number {
  if (count <= 1) return PAD_LEFT + PLOT_W / 2;
  return PAD_LEFT + PLOT_W / (count * 2) + (index * PLOT_W) / count;
}

function yFor(hours: number, max: number): number {
  if (max <= 0) return PAD_TOP + PLOT_H;
  return PAD_TOP + PLOT_H * (1 - Math.min(1, hours / max));
}

function tooltipTransform(leftPct: number): string {
  if (leftPct < 28) return "translateX(-12px)";
  if (leftPct > 72) return "translateX(calc(-100% + 12px))";
  return "translateX(-50%)";
}

function DemandTooltip({
  month,
  groups,
  expectedHours,
}: {
  month: PodDemandMonth;
  groups: DemandGroup[];
  expectedHours: number;
}) {
  const { abbr, year } = monthLabel(month.month);
  const isActual = month.kind === "actual";
  return (
    <div className="min-w-[188px] max-w-[240px] rounded-md border border-border bg-card px-2.5 py-2 text-sm shadow-md">
      <div className="flex items-center justify-between gap-3">
        <span className="font-semibold text-foreground">
          {abbr} {year}
        </span>
        <span className="text-xs text-muted-foreground">{isActual ? "Logged" : "Projected"}</span>
      </div>
      {month.total > 0 ? (
        <div className="mt-1.5 space-y-1 border-t border-border pt-1.5">
          {groups
            .filter((g) => (month.byGroup.get(g.key) ?? 0) > 0)
            .map((g) => {
              const people = month.contributors.get(g.key) ?? [];
              return (
                <div key={g.key}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span
                        className="h-2 w-2 shrink-0 rounded-sm"
                        style={{ background: g.color }}
                        aria-hidden
                      />
                      <span className="truncate text-foreground">{g.label}</span>
                    </span>
                    <span className="shrink-0 tabular-nums text-foreground">
                      {Math.round(month.byGroup.get(g.key) ?? 0)} hrs
                    </span>
                  </div>
                  {g.key === SUPPORTING_GROUP &&
                    people.map((p) => (
                      <div
                        key={p.resourceId}
                        className="flex justify-between gap-3 pl-3.5 text-xs text-muted-foreground"
                      >
                        <span className="truncate">
                          {p.name}
                          {p.offPod ? " · not assigned" : ""}
                        </span>
                        <span className="shrink-0 tabular-nums">{Math.round(p.hours)}</span>
                      </div>
                    ))}
                </div>
              );
            })}
        </div>
      ) : (
        <div className="mt-1 text-xs text-muted-foreground">
          {isActual ? "No logged hours" : "No guidance"}
        </div>
      )}
      <div className="mt-1.5 flex justify-between gap-3 border-t border-border pt-1.5 text-xs text-muted-foreground">
        <span>Total {Math.round(month.total)} hrs</span>
        <span>Intensity ≈ {Math.round(expectedHours)}</span>
      </div>
    </div>
  );
}

export function PodDemandChart({
  workload,
  isLoading,
  isError,
  onRetry,
  groups,
  groupByResource,
}: {
  workload: SolutionWorkloadPayload | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Bands bottom-up: Primaries in Position order, Supporting last. */
  groups: DemandGroup[];
  groupByResource: Map<string, DemandGroupKey>;
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  if (isLoading) return <Skeleton className="h-[260px] w-full" />;
  if (isError || !workload) {
    return <ErrorState message="Couldn't load this Solution's hours" onRetry={onRetry} />;
  }
  const { actualsEmpty, guidanceUnavailable, lastSyncedAt } = workload;
  const months = groupDemandByPod(workload.months, groupByResource);
  if (months.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-card/50 px-6 py-8 text-center text-sm text-muted-foreground">
        No hours for this Solution yet.
      </div>
    );
  }

  const expectedByMonth = new Map(workload.months.map((m) => [m.month, m.expectedHours]));
  const maxHours = months.reduce(
    (max, m) => Math.max(max, m.total, expectedByMonth.get(m.month) ?? 0),
    1,
  );
  const yMax = maxHours * 1.15;
  const firstGuidanceIndex = months.findIndex((m) => m.kind === "guidance");
  const colW = PLOT_W / months.length;
  const todayX = firstGuidanceIndex > 0 ? PAD_LEFT + firstGuidanceIndex * colW : null;
  const boundaryPct = firstGuidanceIndex === -1 ? 100 : ((todayX ?? PAD_LEFT) / CHART_W) * 100;
  const benchmark = months
    .map((m, i) => `${x(i, months.length)},${yFor(expectedByMonth.get(m.month) ?? 0, yMax)}`)
    .join(" ");
  const hovered = hoverIndex !== null ? months[hoverIndex] : undefined;
  const hoverLeftPct = hoverIndex !== null ? (x(hoverIndex, months.length) / CHART_W) * 100 : 0;
  const now = new Date();
  const stale = lastSyncedAt
    ? now.getTime() - new Date(lastSyncedAt).getTime() > STALE_AFTER_MS
    : false;
  const groupsWithHours = groups.filter((g) => months.some((m) => (m.byGroup.get(g.key) ?? 0) > 0));

  return (
    <div className="space-y-2">
      <div className="relative rounded-md border border-border bg-card p-2">
        <svg
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          className="h-52 w-full"
          onMouseLeave={() => setHoverIndex(null)}
        >
          <title>Hours by Pod member — logged behind today, projected ahead</title>
          {Y_TICKS.map((frac) => {
            const gy = PAD_TOP + PLOT_H * (1 - frac);
            return (
              <g key={frac}>
                <line
                  x1={PAD_LEFT}
                  y1={gy}
                  x2={CHART_W - PAD_RIGHT}
                  y2={gy}
                  stroke="var(--border)"
                  strokeDasharray={frac === 0 ? "0" : "2 3"}
                />
                <text
                  x={PAD_LEFT - 5}
                  y={gy + 3}
                  fill="var(--muted-foreground)"
                  textAnchor="end"
                  style={{ fontSize: "var(--fs-chart-label)", fontFamily: "var(--font-sans)" }}
                >
                  {Math.round(frac * yMax)}
                </text>
              </g>
            );
          })}

          {hoverIndex !== null && (
            <rect
              x={PAD_LEFT + hoverIndex * colW}
              y={PAD_TOP}
              width={colW}
              height={PLOT_H}
              fill="var(--foreground)"
              fillOpacity="0.04"
            />
          )}

          {months.map((m, i) => {
            const projected = m.kind === "guidance";
            let base = PAD_TOP + PLOT_H;
            return (
              <g key={m.month}>
                {groups.map((g) => {
                  const hours = m.byGroup.get(g.key) ?? 0;
                  if (hours <= 0) return null;
                  const h = (hours / yMax) * PLOT_H;
                  base -= h;
                  return (
                    <rect
                      key={g.key}
                      x={x(i, months.length) - BAR_W / 2}
                      y={base}
                      width={BAR_W}
                      height={Math.max(0, h)}
                      fill={projected ? projectedTint(g.color) : g.color}
                      stroke="var(--card)"
                      strokeWidth="1"
                    />
                  );
                })}
              </g>
            );
          })}

          <polyline
            points={benchmark}
            fill="none"
            stroke="var(--muted-foreground)"
            strokeWidth="1.5"
            strokeDasharray="5 3"
            strokeLinejoin="round"
          />

          {todayX !== null && (
            <g>
              <line
                x1={todayX}
                y1={PAD_TOP - 6}
                x2={todayX}
                y2={PAD_TOP + PLOT_H}
                stroke="var(--foreground)"
                strokeOpacity="0.5"
              />
              <text
                x={todayX + 4}
                y={PAD_TOP - 8}
                fill="var(--muted-foreground)"
                style={{ fontSize: "var(--fs-chart-label)", fontFamily: "var(--font-sans)" }}
              >
                Projected →
              </text>
              <text
                x={todayX - 4}
                y={PAD_TOP - 8}
                fill="var(--muted-foreground)"
                textAnchor="end"
                style={{ fontSize: "var(--fs-chart-label)", fontFamily: "var(--font-sans)" }}
              >
                ← Logged
              </text>
            </g>
          )}

          {months.map((m, i) => {
            const { abbr, year } = monthLabel(m.month);
            const isCurrent = i === firstGuidanceIndex;
            return (
              <text
                key={`axis-${m.month}`}
                x={x(i, months.length)}
                y={CHART_H - PAD_BOTTOM + 14}
                fill={isCurrent ? "var(--foreground)" : "var(--muted-foreground)"}
                fontWeight={isCurrent ? 700 : 400}
                textAnchor="middle"
                style={{ fontSize: "var(--fs-chart-label)", fontFamily: "var(--font-sans)" }}
              >
                {abbr}
                {abbr === "Jan" || i === 0 ? ` '${String(year).slice(2)}` : ""}
              </text>
            );
          })}

          {months.map((m, i) => (
            <rect
              key={`hover-${m.month}`}
              x={PAD_LEFT + i * colW}
              y={PAD_TOP}
              width={colW}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() => setHoverIndex(i)}
            />
          ))}
        </svg>

        {hovered && hoverIndex !== null && (
          <div
            className="pointer-events-none absolute top-1 z-10"
            style={{ left: `${hoverLeftPct}%`, transform: tooltipTransform(hoverLeftPct) }}
          >
            <DemandTooltip
              month={hovered}
              groups={groups}
              expectedHours={expectedByMonth.get(hovered.month) ?? 0}
            />
          </div>
        )}

        {actualsEmpty && (
          <div
            className="pointer-events-none absolute left-0 top-8 flex justify-center px-2 text-center"
            style={{ width: `${boundaryPct}%` }}
          >
            <span className="rounded bg-card/90 px-2 py-1 text-xs text-muted-foreground">
              No linked NOS Demands yet
            </span>
          </div>
        )}
        {guidanceUnavailable && (
          <div
            className="pointer-events-none absolute right-0 top-8 flex justify-center px-2 text-center"
            style={{ left: `${boundaryPct}%` }}
          >
            <span className="rounded bg-card/90 px-2 py-1 text-xs text-muted-foreground">
              NOS guidance unavailable
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {groupsWithHours.map((g) => (
          <span key={g.key} className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: g.color }} aria-hidden />
            <span className="text-foreground">{g.label}</span>
          </span>
        ))}
        <span
          className="flex items-center gap-1.5"
          title={`Intensity × ${workload.hoursPerIntensityPoint} hrs — a reference line only`}
        >
          <span className="inline-block h-0 w-3.5 border-t-2 border-dashed border-muted-foreground" />
          Intensity
        </span>
        <span
          className={`ml-auto ${stale ? "text-[var(--fit-moderate)]" : ""}`}
          title={
            lastSyncedAt
              ? `Logged hours last synced ${new Date(lastSyncedAt).toLocaleString()}`
              : "Logged hours have not synced yet"
          }
        >
          {lastSyncedAt
            ? `Synced ${relativeTimeFrom(lastSyncedAt, now)}${stale ? " (stale)" : ""}`
            : "Never synced"}
        </span>
      </div>
    </div>
  );
}

/** One Resource's logged vs projected hours on this Solution, with a tiny 13-month strip. */
export function MemberDemand({
  workload,
  resourceId,
  color,
}: {
  workload: SolutionWorkloadPayload | undefined;
  resourceId: string;
  color: string;
}) {
  if (!workload) return null;
  const s = summarizeResourceDemand(workload.months, resourceId, workload);
  const fmt = (v: number | null) => (v === null ? "—" : `${Math.round(v)}`);
  const max = Math.max(1, ...s.series.map((p) => p.hours ?? 0));
  return (
    <div className="space-y-1">
      <div
        className="whitespace-nowrap text-xs text-muted-foreground"
        title="Average hours per month: logged over the last 6 months, NOS guidance for this month and the next 6"
      >
        <span className="font-semibold tabular-nums text-foreground">{fmt(s.loggedPerMonth)}</span>{" "}
        logged ·{" "}
        <span className="font-semibold tabular-nums text-foreground">
          {fmt(s.projectedPerMonth)}
        </span>{" "}
        projected hrs/mo
      </div>
      <svg
        viewBox={`0 0 ${s.series.length * 6} 16`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Monthly hours, logged then projected"
        className="block h-4 w-full"
      >
        {s.series.map((p, i) => {
          const h = p.hours === null ? 0 : Math.max(p.hours > 0 ? 1.5 : 0, (p.hours / max) * 16);
          return (
            <rect
              key={p.month}
              x={i * 6 + 0.5}
              y={16 - h}
              width="5"
              height={h}
              fill={p.kind === "guidance" ? projectedTint(color) : color}
            />
          );
        })}
      </svg>
    </div>
  );
}
